'use client';

import { useLayoutEffect, useRef, useState } from 'react';

/** Swatch and overflow control footprint — both stay 18px including when selected. */
const SWATCH_SIZE_PX = 18;
const SWATCH_GAP_PX = 6;
const OVERFLOW_INDICATOR_SIZE_PX = 18;

export function getVisibleSwatchCount(containerWidth: number, total: number): number {
    if (total <= 0) return 0;
    if (containerWidth <= 0) return total;

    const swatchStep = SWATCH_SIZE_PX + SWATCH_GAP_PX;
    const fitsAll = Math.floor((containerWidth + SWATCH_GAP_PX) / swatchStep);
    if (fitsAll >= total) return total;

    const reservedForIndicator = OVERFLOW_INDICATOR_SIZE_PX + SWATCH_GAP_PX;
    const withIndicator = Math.floor((containerWidth - reservedForIndicator + SWATCH_GAP_PX) / swatchStep);

    return Math.max(1, Math.min(withIndicator, total));
}

export function useVisibleSwatchCount(total: number) {
    const containerRef = useRef<HTMLDivElement>(null);
    const [visibleCount, setVisibleCount] = useState(total);

    useLayoutEffect(() => {
        const element = containerRef.current;
        if (!element) return;

        const update = () => {
            setVisibleCount(getVisibleSwatchCount(element.clientWidth, total));
        };

        update();

        if (typeof ResizeObserver === 'undefined') return;

        const observer = new ResizeObserver(update);
        observer.observe(element);

        return () => observer.disconnect();
    }, [total]);

    return { containerRef, visibleCount };
}
